import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "../../../assets/css/gcstudent/librarypage/LibraryPage.css";
import BookDetailPage from "../bookdetailpage/BookDetailPage";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import BookCard from "../../../components/gcstudent/bookCard/BookCard";

const CATEGORIES = [
  "All",
  "Computer Science",
  "Information Technology",
  "Mathematics",
  "Education",
  "Psychology",
  "Islamiyat",
  "Physics",
  "English",
];

const PAGE_SIZE = 8;

// ✅ selectedBook / setSelectedBook now come from the parent (GcLibrary)
// instead of being local state, so the parent can hide the hero section
// (AboutDigiLib) when a book detail view is active.
const LibraryPage = ({ selectedBook, setSelectedBook }) => {
  const [allBooks,       setAllBooks]       = useState([]);
  const [filtered,       setFiltered]       = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery,    setSearchQuery]    = useState("");
  const [animating,      setAnimating]      = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [visibleCount,   setVisibleCount]   = useState(PAGE_SIZE);

  // ✅ authToken now comes from AppContext, not localStorage.
  // Library is public (browsing books doesn't require login), so we
  // still fetch even when authToken is empty — backend route is open.
  const { baseUrl, authToken } = useContext(AppContext);

  /* ── Filter helper ── (moved above fetchBooks so fetchBooks can call it) */
  const applyFilter = (books, category, query) => {
    let result = category === "All"
      ? books
      : books.filter(b => b.dept === category);

    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter(b =>
        b.title?.toLowerCase().includes(q)  ||
        b.author?.toLowerCase().includes(q) ||
        b.dept?.toLowerCase().includes(q)
      );
    }
    return result;
  };

  /* ── Fetch all books ──
     ✅ Re-applies the current category/search filter on top of the fresh
     data, so a refetch (e.g. after returning from the detail page)
     doesn't reset whatever the user was browsing. */
  const fetchBooks = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/general/books/fetchAllBooks`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          const books = (res.data.books || []).filter(Boolean);
          setAllBooks(books);
          setFiltered(applyFilter(books, activeCategory, searchQuery));
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Library fetch error:", err);
      });
  };

  useEffect(() => { fetchBooks(); }, []);

  const handleCategory = (cat) => {
    if (cat === activeCategory) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveCategory(cat);
      setFiltered(applyFilter(allBooks, cat, searchQuery));
      setVisibleCount(PAGE_SIZE);
      setAnimating(false);
    }, 220);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setAnimating(true);
    setTimeout(() => {
      setFiltered(applyFilter(allBooks, activeCategory, query));
      setVisibleCount(PAGE_SIZE);
      setAnimating(false);
    }, 150);
  };

  /* ── Load more 8 ── */
  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  const visibleBooks = filtered.slice(0, visibleCount);
  const hasMore      = visibleCount < filtered.length;

  /* ── Book Detail Page ── */
  if (selectedBook) {
    return (
      <BookDetailPage
        book={selectedBook}
        onBack={() => {
          setSelectedBook(null);
          fetchBooks(); // ✅ re-fetch so the grid reflects any reservation just made
        }}
      />
    );
  }

  return (
    <div className="gcStudent-library-page">
      <Container fluid="lg">

     {/* SEARCH BAR */}
        <div className="gcStudent-notes-search-wrap">
          <div className="gcStudent-notes-search-inner">
            <input
              type="text"
              className="gcStudent-notes-search-input"
              placeholder="Search by title, subject or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="gcStudent-notes-search-btn">Search</button>
          </div>
        </div>

        {/* ── Filter Tabs ── */}
        <div className="gcStudent-filter-row">
          <div className="gcStudent-filter-scroll">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`gcStudent-filter-btn ${activeCategory === cat ? "gcStudent-filter-active" : ""}`}
                onClick={() => handleCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* ── Book Grid ── */}
       {loading ? (
  <Row className="gy-4 gx-3">
    {Array.from({ length: PAGE_SIZE }).map((_, i) => (
      <Col key={`skeleton-${i}`} xs={12} sm={6} md={4} lg={3}>
        <div className="gcStudent-skeleton-card">
          <div className="gcStudent-skeleton-img" />
          <div className="gcStudent-skeleton-info">
            <div className="gcStudent-skeleton-line gcStudent-skeleton-badge" />
            <div className="gcStudent-skeleton-line gcStudent-skeleton-title" />
            <div className="gcStudent-skeleton-line gcStudent-skeleton-title-short" />
            <div className="gcStudent-skeleton-line gcStudent-skeleton-author" />
            <div className="gcStudent-skeleton-line gcStudent-skeleton-avail" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
) : (
  <>
    <div className={`gcStudent-books-grid ${animating ? "gcStudent-grid-exit" : "gcStudent-grid-enter"}`}>
      {visibleBooks.length === 0 ? (
        <div className="gcStudent-no-results">
          <p>
            {searchQuery
              ? `No books found matching "${searchQuery}"`
              : "No books available in this category."}
          </p>
        </div>
      ) : (
        <Row className="gy-4 gx-3">
          {visibleBooks.map((book, i) => (
            <Col key={book.id} xs={12} sm={6} md={4} lg={3}>
              <BookCard book={book} index={i} onClick={setSelectedBook} />
            </Col>
          ))}
        </Row>
      )}
    </div>

    {hasMore && (
      <div className="gcStudent-load-more-row">
        <button
          className="gcStudent-load-more-btn"
          onClick={handleLoadMore}
        >
          Load More Books
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12l7 7 7-7"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    )}
  </>
   )}

      </Container>
    </div>
  );
};

export default LibraryPage;